import { modal } from '../ui/modal.js';
import { toast } from '../ui/toast.js';
import { refresh } from '../ui/views.js';
import { dbPut, normalizeCard, normalizeDeck, tx } from './db.js';
import { scopedCards } from './load.js';
import { state } from './state.js';
import { $, dayKey, esc, uid } from './text.js';

/* ══════════ Sicherung ══════════ */
export function setBackupStatus(msg, kind = '') { const el = $('#backup-status'); el.textContent = msg; el.className = 'status ' + kind; }
export function download(dump, name) {
  const blob = new Blob([JSON.stringify(dump)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
  return blob.size;
}
export function exportData(scopeOnly) {
  let courses = state.courses, decks = state.decks, cards = state.cards, name = 'mimorio';
  if (scopeOnly) {
    if (!state.selCourse) { setBackupStatus('Erst einen Kurs oder eine Vorlesung auswählen.', 'err'); return; }
    cards = scopedCards();
    const deckIds = new Set(cards.map((c) => c.deckId));
    decks = state.decks.filter((d) => deckIds.has(d.id));
    const courseIds = new Set(decks.map((d) => d.courseId));
    courses = state.courses.filter((c) => courseIds.has(c.id));
    name = 'lernset-' + (state.courses.find((c) => c.id === state.selCourse)?.name || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
  if (!cards.length) { setBackupStatus('Da sind keine Karten drin.', 'err'); return; }
  const size = download({ format: 'karteikarten', version: 2, exported: new Date().toISOString(), courses, decks, cards }, `${name}-${dayKey()}.json`);
  setBackupStatus(`Gespeichert: ${cards.length} Karten, ${decks.length} Vorlesungen, ${courses.length} Kurse (${(size / 1048576).toFixed(2)} MB).`, 'ok');
  toast(scopeOnly ? 'Lernset gespeichert ✦' : 'Sicherung gespeichert ✦');
}
export function shareCourse(courseId) {
  const course = state.courses.find((c) => c.id === courseId);
  if (!course) return;
  const decks = state.decks.filter((d) => d.courseId === courseId);
  const deckIds = new Set(decks.map((d) => d.id));
  const cards = state.cards.filter((c) => deckIds.has(c.deckId));
  if (!cards.length) { toast('Dieser Kurs hat noch keine Karten zum Teilen'); return; }
  const name = 'lernset-' + (course.name || 'kurs').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  download({ format: 'karteikarten', version: 2, exported: new Date().toISOString(), courses: [course], decks, cards }, `${name}-${dayKey()}.json`);
  toast(`„${course.name}" als Datei geteilt ✦ (${cards.length} Karten)`);
}
export async function importData(file) {
  let dump;
  try { dump = JSON.parse(await file.text()); } catch { setBackupStatus('Keine gültige Sicherungsdatei.', 'err'); return; }
  if (dump.format !== 'karteikarten' || !Array.isArray(dump.cards)) { setBackupStatus('Keine Karteikarten-Sicherung.', 'err'); return; }
  const when = dump.exported ? new Date(dump.exported).toLocaleString('de') : 'unbekannt';
  modal({
    title: 'Datei einspielen', okText: 'Einspielen',
    body: `<p class="page-sub">Enthält <strong>${dump.cards.length} Karten</strong> in ${dump.decks?.length || 0} Vorlesungen, ${dump.courses?.length || 0} Kursen.<br>Erstellt am ${esc(when)}.</p>
           <div class="radio-row">
             <label class="radio-opt"><input type="radio" name="imp" value="merge" checked><span><strong>Dazufügen</strong>Deine Karten bleiben, die aus der Datei kommen dazu.</span></label>
             <label class="radio-opt"><input type="radio" name="imp" value="replace"><span><strong>Ersetzen</strong>Löscht erst alles Vorhandene. Aktuell hast du ${state.cards.length} Karten.</span></label>
           </div>`,
    onOk: async () => {
      const mode = document.querySelector('input[name="imp"]:checked').value;
      if (mode === 'replace' && state.cards.length && !confirm(`Wirklich alle ${state.cards.length} vorhandenen Karten löschen und ersetzen?`)) return false;
      await applyImport(dump, mode);
    },
  });
}
export async function applyImport(dump, mode) {
  if (mode === 'replace') {
    for (const s of ['cards', 'decks', 'courses']) await new Promise((r) => { tx(s, 'readwrite').clear().onsuccess = r; });
    state.cards = []; state.decks = []; state.courses = []; state.selCourse = null; state.selDecks.clear(); state.openCourses.clear();
  }
  const idMap = new Map();
  const remap = (old) => { if (!idMap.has(old)) idMap.set(old, uid()); return idMap.get(old); };
  for (const c of dump.courses || []) { const rec = { ...c, id: remap(c.id), created: c.created ?? Date.now(), archived: !!c.archived }; await dbPut('courses', rec); state.courses.push(rec); }
  for (const d of dump.decks || []) { const rec = normalizeDeck({ ...d, id: remap(d.id), courseId: remap(d.courseId), created: d.created ?? Date.now() }); await dbPut('decks', rec); state.decks.push(rec); }
  let skipped = 0;
  for (const c of dump.cards || []) {
    if (!idMap.has(c.deckId) || !idMap.has(c.courseId)) { skipped++; continue; }
    const rec = normalizeCard({ ...c, id: uid(), courseId: remap(c.courseId), deckId: remap(c.deckId) });
    await dbPut('cards', rec); state.cards.push(rec);
  }
  state.courses.sort((a, b) => a.created - b.created); state.decks.sort((a, b) => a.created - b.created);
  if (!state.selCourse && state.courses.length) { state.selCourse = state.courses[0].id; state.openCourses.add(state.courses[0].id); }
  const n = (dump.cards?.length || 0) - skipped;
  setBackupStatus(`${n} Karten eingespielt${skipped ? ` · ${skipped} übersprungen` : ''}.`, 'ok');
  toast(`${n} Karten eingespielt ✦`); refresh();
}
export async function guardStorage() {
  const note = $('#storage-note');
  if (!navigator.storage?.persist) { note.textContent = ''; return; }
  try {
    const ok = (await navigator.storage.persisted()) || (await navigator.storage.persist());
    const est = await navigator.storage.estimate?.();
    const used = est?.usage ? ` · aktuell ${(est.usage / 1048576).toFixed(1)} MB belegt` : '';
    note.textContent = ok
      ? `Speicher ist dauerhaft geschützt${used}.`
      : `Der Browser hat den Speicher nicht dauerhaft geschützt. Mach ab und zu eine Sicherung${used}.`;
  } catch { note.textContent = ''; }
}

